'use client';

import { playMedalFeedback } from '@/hooks/useTtsPlayer';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Award, Check, Image as ImageIcon, Trophy, X } from 'lucide-react';
import { auth } from '@/firebaseConfig';
import AcronymInput from '@/components/AcronymInput';
import AndroidHomeVerticalSubslides from '@/components/AndroidHomeVerticalSubslides';
import FitText from '@/components/FitText';
import SiteFooter from '@/components/SiteFooter';
import DailyVoteBoard, { DAILY_VOTE_MEDALS, buildNextVoteMap } from '@/components/daily/DailyVoteBoard';
import { getDailyLettersFitMobile } from '@/lib/androidDailyTextFit';
import { getAndroidWinnerAnswerFontSizePx } from '@/lib/androidWinnerTextFit';
import { applyLeadingCaps } from '@/lib/dailySubmission';
import { PICTONYM_ANDROID_CLAIM_XP } from '@/lib/pictonymXpClaims';
import AndroidWeeklyImageViewer from '@/components/pictonym/AndroidWeeklyImageViewer';
import { readAndroidHomeStatusCache, writeAndroidHomeStatusCache } from '@/lib/androidHomeStatusCache';
import { buildPictonymPhaseState } from '@/lib/pictonymCycle';
import { seededShuffle } from '@/utils/seededShuffle';

const ANDROID_HOME_STATUS_PATCH_EVENT = 'acroverse:android-home-status-patch';

const preloadPictonymImage = (src) => new Promise((resolve) => {
  if (!src || typeof window === 'undefined') {
    resolve();
    return;
  }

  const image = new window.Image();
  let settled = false;
  const timeout = window.setTimeout(() => finish(), 8000);
  const finish = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeout);
    resolve();
  };
  image.onload = () => {
    if (typeof image.decode === 'function') {
      image.decode().catch(() => {}).finally(finish);
      return;
    }
    finish();
  };
  image.onerror = finish;
  image.src = src;
});

const formatCountdown = (deadlineAt, now) => {
  const seconds = Math.max(0, Math.ceil((Number(deadlineAt || 0) - now) / 1000));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return days ? `${days}d ${hours}h ${minutes}m` : `${hours}h ${minutes}m`;
};

export const buildRoundedNookPath = ({ width, height, topHalf, topDepth, bottomHalf, bottomDepth }) => {
  const radius = 5;
  const centre = width / 2;
  const topLeft = centre - topHalf;
  const topRight = centre + topHalf;
  const bottomLeft = centre - bottomHalf;
  const bottomRight = centre + bottomHalf;
  const bottomTop = height - bottomDepth;
  const value = (number) => Math.round(number * 10) / 10;
  const path = [`M ${radius} 0`];
  if (topDepth > 0 && topHalf > 0) {
    path.push(
      `L ${value(topLeft - radius)} 0`,
      `Q ${value(topLeft)} 0 ${value(topLeft)} ${radius}`,
      `L ${value(topLeft)} ${value(topDepth - radius)}`,
      `Q ${value(topLeft)} ${value(topDepth)} ${value(topLeft + radius)} ${value(topDepth)}`,
      `L ${value(topRight - radius)} ${value(topDepth)}`,
      `Q ${value(topRight)} ${value(topDepth)} ${value(topRight)} ${value(topDepth - radius)}`,
      `L ${value(topRight)} ${radius}`,
      `Q ${value(topRight)} 0 ${value(topRight + radius)} 0`,
    );
  }
  path.push(
    `L ${value(width - radius)} 0`,
    `Q ${value(width)} 0 ${value(width)} ${radius}`,
    `L ${value(width)} ${value(height)}`,
  );
  if (bottomDepth > 0 && bottomHalf > 0) {
    path.push(
      `L ${value(bottomRight + radius)} ${value(height)}`,
      `Q ${value(bottomRight)} ${value(height)} ${value(bottomRight)} ${value(height - radius)}`,
      `L ${value(bottomRight)} ${value(bottomTop + radius)}`,
      `Q ${value(bottomRight)} ${value(bottomTop)} ${value(bottomRight - radius)} ${value(bottomTop)}`,
      `L ${value(bottomLeft + radius)} ${value(bottomTop)}`,
      `Q ${value(bottomLeft)} ${value(bottomTop)} ${value(bottomLeft)} ${value(bottomTop + radius)}`,
      `L ${value(bottomLeft)} ${value(height - radius)}`,
      `Q ${value(bottomLeft)} ${value(height)} ${value(bottomLeft - radius)} ${value(height)}`,
    );
  }
  path.push(
    `L 0 ${value(height)}`,
    `L 0 ${radius}`,
    `Q 0 0 ${radius} 0`,
    'Z',
  );
  return path.join(' ');
};

export const buildRoundedNookBorderPath = ({ width, height, topHalf, topDepth, bottomHalf, bottomDepth }) => {
  const inset = 0.5;
  const radius = 5;
  const centre = width / 2;
  const topLeft = centre - topHalf;
  const topRight = centre + topHalf;
  const bottomLeft = centre - bottomHalf;
  const bottomRight = centre + bottomHalf;
  const bottom = height - inset;
  const bottomTop = height - bottomDepth;
  const value = (number) => Math.round(number * 10) / 10;
  const path = [
    `M ${inset} ${radius}`,
    `Q ${inset} ${inset} ${radius} ${inset}`,
  ];
  if (topDepth > 0 && topHalf > 0) {
    path.push(
      `L ${value(topLeft - radius)} ${inset}`,
      `Q ${value(topLeft)} ${inset} ${value(topLeft)} ${radius}`,
      `L ${value(topLeft)} ${value(topDepth - radius)}`,
      `Q ${value(topLeft)} ${value(topDepth)} ${value(topLeft + radius)} ${value(topDepth)}`,
      `L ${value(topRight - radius)} ${value(topDepth)}`,
      `Q ${value(topRight)} ${value(topDepth)} ${value(topRight)} ${value(topDepth - radius)}`,
      `L ${value(topRight)} ${radius}`,
      `Q ${value(topRight)} ${inset} ${value(topRight + radius)} ${inset}`,
    );
  }
  path.push(
    `L ${value(width - radius)} ${inset}`,
    `Q ${value(width - inset)} ${inset} ${value(width - inset)} ${radius}`,
    `L ${value(width - inset)} ${bottom}`,
  );
  if (bottomDepth > 0 && bottomHalf > 0) {
    path.push(
      `L ${value(bottomRight + radius)} ${bottom}`,
      `Q ${value(bottomRight)} ${bottom} ${value(bottomRight)} ${value(height - radius)}`,
      `L ${value(bottomRight)} ${value(bottomTop + radius)}`,
      `Q ${value(bottomRight)} ${value(bottomTop)} ${value(bottomRight - radius)} ${value(bottomTop)}`,
      `L ${value(bottomLeft + radius)} ${value(bottomTop)}`,
      `Q ${value(bottomLeft)} ${value(bottomTop)} ${value(bottomLeft)} ${value(bottomTop + radius)}`,
      `L ${value(bottomLeft)} ${value(height - radius)}`,
      `Q ${value(bottomLeft)} ${bottom} ${value(bottomLeft - radius)} ${bottom}`,
    );
  }
  path.push(
    `L ${inset} ${bottom}`,
    `L ${inset} ${radius}`,
  );
  return path.join(' ');
};

const AndroidLayeredLetters = ({ letters }) => (
  <span className="androidAppHomeStatic__featureLetters" data-android-home-fit-text="letters">
    <span className="androidAppHomeStatic__lettersGlyph" data-text={letters || '...'}>
      <span className="androidAppHomeStatic__lettersInset androidAppHomeStatic__lettersInset--a" aria-hidden="true">{letters || '...'}</span>
      <span className="androidAppHomeStatic__lettersInset androidAppHomeStatic__lettersInset--b" aria-hidden="true">{letters || '...'}</span>
      <span className="androidAppHomeStatic__lettersInset androidAppHomeStatic__lettersInset--c" aria-hidden="true">{letters || '...'}</span>
      <span className="androidAppHomeStatic__lettersInset androidAppHomeStatic__lettersInset--d" aria-hidden="true">{letters || '...'}</span>
      <span className="androidAppHomeStatic__lettersInset androidAppHomeStatic__lettersInset--e" aria-hidden="true">{letters || '...'}</span>
      <span className="androidAppHomeStatic__lettersFace">{letters || '...'}</span>
    </span>
  </span>
);

const DesktopLayeredLetters = ({ letters }) => (
  <FitText
    as="h3"
    className="acronym pictonymEnterScreen__acronym"
    data-letters={String(Math.max(2, Math.min(9, String(letters || '').length || 3)))}
    compressor={(String(letters || '').length || 3) * 0.1}
    maxFontSize={112}
  >
    <span className="dailyEnterScreen__acronymGlyph" data-text={letters || ''}>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--a" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--b" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--c" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--d" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--e" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--f" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--g" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--h" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymInset dailyEnterScreen__acronymInset--i" aria-hidden="true">{letters}</span>
      <span className="dailyEnterScreen__acronymFace">{letters}</span>
    </span>
  </FitText>
);

export const DesktopPictonymChallenge = ({ challenge, imageUrl, onOpenImage }) => (
  <section className="promptSection pictonymEnterScreen__challenge" aria-label="This week’s Weekly Pictonym picture and acronym">
    <div className="pictonymEnterScreen__media">
      <button
        type="button"
        className="pictonymEnterScreen__imageButton"
        onClick={onOpenImage}
        aria-label="Open Weekly Pictonym picture full screen"
      >
        <img
          className="pictonymEnterScreen__image"
          src={imageUrl}
          alt={challenge.imageAlt || 'Weekly Pictonym challenge'}
        />
      </button>
    </div>
    <div className="pictonymEnterScreen__letters">
      <p>The acronym is:</p>
      <DesktopLayeredLetters letters={challenge.letters} />
    </div>
  </section>
);

export const DesktopPictonymHeader = ({ challenge }) => (
  <div className="dailyEnterScreen__header">
    <Link href="/" className="dailyPage__closeLink" aria-label="Back to home" title="Back to home">
      <X size={16} strokeWidth={1.8} />
    </Link>
    <h1 className="dailyEnterScreen__title">
      Weekly Pictonym
      <span className="dailyEnterScreen__titleDate">{challenge.label || 'This Week'}</span>
    </h1>
  </div>
);

export const AndroidWeeklyPicturePrompt = ({ challenge = null, loading = false, showLetters = true, pictureStageRef = null, lettersRef = null, borderSvgRef = null, borderPathRef = null, onOpenImage = null }) => {
  const PictureStage = onOpenImage ? 'button' : 'div';
  return (
    <div className="androidAppHomeStatic__featureBody androidAppHomeEnterCard__promptBlock weeklyPictonymEnterCard__promptBlock">
      <div className="weeklyPictonymEnterCard__pictureFrame">
        <PictureStage ref={pictureStageRef} type={onOpenImage ? 'button' : undefined} className={`weeklyPictonymEnterCard__pictureStage${onOpenImage ? ' weeklyPictonymEnterCard__pictureStage--interactive' : ''}${loading ? ' androidAppHomeStatic__skeleton' : ''}`} onClick={onOpenImage || undefined} aria-label={onOpenImage ? 'Open Weekly Pictonym picture full screen' : undefined}>
          {challenge ? (
            <>
              <img className="weeklyPictonymEnterCard__pictureAmbient" src={challenge.imageUrl} alt="" aria-hidden="true" />
              <img className="weeklyPictonymEnterCard__picture" src={challenge.imageUrl} alt={challenge.imageAlt} />
            </>
          ) : null}
        </PictureStage>
        <svg ref={borderSvgRef} className="weeklyPictonymEnterCard__pictureBorder" aria-hidden="true" preserveAspectRatio="none">
          <path ref={borderPathRef} />
        </svg>
      </div>
      {showLetters ? (
        <div ref={lettersRef} className="weeklyPictonymEnterCard__lettersOverlap">
          <AndroidLayeredLetters letters={challenge?.letters || '...'} />
        </div>
      ) : null}
    </div>
  );
};

export const AndroidWeeklyTitle = ({ titleRef = null, children = 'Weekly Picto' }) => (
  <span ref={titleRef} className="androidAppHomeStatic__missionTitle androidAppHomeStatic__featureSlideHeading weeklyPictonymEnterCard__titleNook">
    <span className="androidAppHomeStatic__missionTitleInset androidAppHomeStatic__missionTitleInset--a" aria-hidden="true">{children}</span>
    <span className="androidAppHomeStatic__missionTitleInset androidAppHomeStatic__missionTitleInset--b" aria-hidden="true">{children}</span>
    <span className="androidAppHomeStatic__missionTitleFace">{children}</span>
  </span>
);

export default function PictonymPageClient({ embedded = false, view = '' } = {}) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [answer, setAnswer] = useState('');
  const [isEditing, setIsEditing] = useState(true);
  const [votes, setVotes] = useState({ goldSubmissionId: '', silverSubmissionId: '', bronzeSubmissionId: '' });
  const [now, setNow] = useState(() => Date.now());
  const [xpMessage, setXpMessage] = useState('');
  const [embeddedVoteExpanded, setEmbeddedVoteExpanded] = useState(false);
  const [weeklyInputError, setWeeklyInputError] = useState('');
  const [androidImageViewer, setAndroidImageViewer] = useState(null);
  const [viewerUid, setViewerUid] = useState('');
  const weeklyInputErrorTimerRef = useRef(null);
  const weeklyVoteSaveTimerRef = useRef(null);
  const weeklyVotesRef = useRef(votes);
  const weeklyTitleRef = useRef(null);
  const weeklyPictureStageRef = useRef(null);
  const weeklyLettersRef = useRef(null);
  const weeklyVoteAnswerRef = useRef(null);
  const weeklyBorderSvgRef = useRef(null);
  const weeklyBorderPathRef = useRef(null);
  const inputElementRef = useRef(null);
  const savedAnswerRef = useRef('');

  const applyWeeklyPayload = useCallback((payload) => {
    setState(payload);
    const loadedAnswer = applyLeadingCaps(String(payload?.participation?.submissionText || ''));
    savedAnswerRef.current = loadedAnswer;
    setAnswer(loadedAnswer);
    setIsEditing(payload?.participation?.hasSubmitted !== true);
    setVotes(payload?.participation?.voteSelection || { goldSubmissionId: '', silverSubmissionId: '', bronzeSubmissionId: '' });
    const weeklyPhase = payload?.phase === 'vote' ? 'vote' : 'entry';
    const weeklyComplete = weeklyPhase === 'vote'
      ? payload?.participation?.hasVoted === true
      : payload?.participation?.hasSubmitted === true;
    const weeklyEntryCount = Math.max(0, Number(payload?.metrics?.entryCount || 0));
    const weeklyVoteCount = Math.max(0, Number(payload?.metrics?.medalAwardCount || 0));
    const cacheUid = auth.currentUser?.uid || '';
    const currentCache = readAndroidHomeStatusCache(cacheUid) || {};
    writeAndroidHomeStatusCache(cacheUid, {
      ...currentCache,
      weeklyState: payload,
      weeklyPhase,
      weeklyComplete,
      weeklyHasSubmitted: payload?.participation?.hasSubmitted === true,
      weeklyHasVoted: payload?.participation?.hasVoted === true,
    });
    window.dispatchEvent(new CustomEvent(ANDROID_HOME_STATUS_PATCH_EVENT, {
      detail: {
        weeklyState: payload,
        weeklyPhase,
        weeklyComplete,
        weeklyHasSubmitted: payload?.participation?.hasSubmitted === true,
        weeklyHasVoted: payload?.participation?.hasVoted === true,
        weeklyMetaLabel: `${weeklyEntryCount} ${weeklyEntryCount === 1 ? 'entry' : 'entries'}${weeklyPhase === 'vote' ? `, ${weeklyVoteCount} ${weeklyVoteCount === 1 ? 'vote' : 'votes'}` : ''}`,
        weeklyStateLabel: weeklyComplete
          ? (weeklyPhase === 'vote'
            ? (payload?.participation?.hasSubmitted === true ? 'Entered & voted' : 'You voted')
            : 'You entered')
          : (weeklyPhase === 'vote' ? 'Vote now' : 'Enter now'),
        xpClaims: payload?.xpClaim ? { weekly: payload.xpClaim } : undefined,
      },
    }));
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken?.().catch(() => '');
      const response = await fetch('/api/pictonym/state', { cache: 'no-store', headers: token ? { authorization: `Bearer ${token}` } : {} });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Weekly Pictonym is unavailable');
      await preloadPictonymImage(payload?.challenge?.imageUrl);
      applyWeeklyPayload(payload);
      setError('');
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setLoading(false);
    }
  }, [applyWeeklyPayload]);

  useEffect(() => {
    let active = true;
    const initialise = (user) => {
      if (!active) return;
      setViewerUid(String(user?.uid || ''));
      const cachedWeeklyState = embedded
        ? readAndroidHomeStatusCache(user?.uid || '')?.weeklyState
        : null;
      const expectedState = buildPictonymPhaseState(Date.now());
      const expectedCycleId = expectedState.activeCycleId;
      const cachedCycleId = String(cachedWeeklyState?.cycle?.activeCycleId || '').trim();
      const cachedPhase = cachedWeeklyState?.phase === 'vote' ? 'vote' : 'entry';
      const canPaintCache = Boolean(
        cachedWeeklyState
        && cachedCycleId === expectedCycleId
        && cachedPhase === expectedState.phase
      );
      if (canPaintCache) {
        applyWeeklyPayload(cachedWeeklyState);
        setLoading(false);
        void preloadPictonymImage(cachedWeeklyState?.challenge?.imageUrl);
      }
      void load({ background: canPaintCache });
    };

    // Firebase can already know the signed-in user before the observer callback is
    // delivered. Start immediately so client-side navigation from the homepage
    // cannot sit on the loading panel waiting for an auth event.
    if (auth.currentUser) initialise(auth.currentUser);
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user === auth.currentUser && state) return;
      initialise(user);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyWeeklyPayload, embedded, load]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!state) return undefined;
    const expected = buildPictonymPhaseState(now);
    const loadedCycleId = String(state?.cycle?.activeCycleId || '').trim();
    const loadedPhase = state?.phase === 'vote' ? 'vote' : 'entry';
    if (loadedCycleId === expected.activeCycleId && loadedPhase === expected.phase) return undefined;
    setState(null);
    setLoading(true);
    void load();
    return undefined;
  }, [load, now, state]);

  useEffect(() => {
    if (!state) return undefined;
    const expected = buildPictonymPhaseState(Date.now());
    const delay = Math.max(0, Number(expected.countdowns?.deadlineAt || 0) - Date.now() + 500);
    const timer = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [state]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const savedAnswer = applyLeadingCaps(String(answer || '').trim());
      const wasSubmitted = state?.participation?.hasSubmitted === true;
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch('/api/pictonym/submit', { method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` }, body: JSON.stringify({ answerText: savedAnswer }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Could not save caption');
      savedAnswerRef.current = savedAnswer;
      setAnswer(savedAnswer);
      const nextWeeklyState = state ? {
        ...state,
        metrics: {
          ...(state.metrics || {}),
          entryCount: Math.max(0, Number(state?.metrics?.entryCount || 0) + (wasSubmitted ? 0 : 1)),
        },
        participation: {
          ...(state.participation || {}),
          hasSubmitted: true,
          submissionText: savedAnswer,
        },
        xpClaim: {
          ...(state.xpClaim || {}),
          claimType: 'entry',
          cycleId: String(payload?.cycleId || state?.cycle?.activeCycleId || ''),
          eligible: true,
          claimed: Boolean(state?.xpClaim?.claimed),
          xp: PICTONYM_ANDROID_CLAIM_XP,
        },
      } : state;
      if (nextWeeklyState) applyWeeklyPayload(nextWeeklyState);
      return true;
    } catch (nextError) { setError(nextError.message); } finally { setBusy(false); }
    return false;
  };

  const activateEditMode = useCallback(() => {
    if (busy || state?.capabilities?.canSubmit !== true) return;
    setError('');
    setWeeklyInputError('');
    setIsEditing(true);
    window.requestAnimationFrame(() => inputElementRef.current?.focus?.());
  }, [busy, state?.capabilities?.canSubmit]);

  const cancelEdit = useCallback(() => {
    setAnswer(savedAnswerRef.current);
    setError('');
    setWeeklyInputError('');
    setIsEditing(false);
  }, []);

  const saveVotes = async (nextVotes = weeklyVotesRef.current) => {
    setBusy(true); setError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch('/api/pictonym/vote', { method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` }, body: JSON.stringify(nextVotes) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Could not save votes');
      const hasAnyVote = Object.values(nextVotes).some(Boolean);
      const previousVoteCount = Object.values(state?.participation?.voteSelection || {}).filter(Boolean).length;
      const nextVoteCount = Object.values(nextVotes).filter(Boolean).length;
      const nextMedalAwardCount = Math.max(
        0,
        Number(state?.metrics?.medalAwardCount || 0) + nextVoteCount - previousVoteCount
      );
      const nextWeeklyState = state ? {
          ...state,
          metrics: {
            ...(state.metrics || {}),
            medalAwardCount: nextMedalAwardCount,
          },
          participation: {
            ...(state.participation || {}),
            hasVoted: hasAnyVote,
            voteSelection: nextVotes,
          },
          xpClaim: {
            ...(state.xpClaim || {}),
            claimType: 'vote',
            cycleId: String(payload?.cycleId || state?.cycle?.activeCycleId || ''),
            eligible: hasAnyVote,
            claimed: Boolean(state?.xpClaim?.claimed),
            xp: PICTONYM_ANDROID_CLAIM_XP,
          },
        } : state;
      if (nextWeeklyState) applyWeeklyPayload(nextWeeklyState);
      return true;
    } catch (nextError) {
      setError(nextError.message);
      return false;
    } finally { setBusy(false); }
  };

  useEffect(() => {
    weeklyVotesRef.current = votes;
  }, [votes]);

  useEffect(() => () => {
    if (!weeklyVoteSaveTimerRef.current) return;
    window.clearTimeout(weeklyVoteSaveTimerRef.current);
    weeklyVoteSaveTimerRef.current = null;
  }, []);

  const claimXp = async (claimType) => {
    setBusy(true); setError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch('/api/pictonym/xp-claim', { method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` }, body: JSON.stringify({ claimType }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Could not claim XP');
      setXpMessage(payload.alreadyClaimed ? 'Weekly XP already claimed' : `+${payload.xpAwarded} XP claimed`);
    } catch (nextError) { setError(nextError.message); } finally { setBusy(false); }
  };

  const weeklyVoteMap = useMemo(() => ({
    gold: String(votes.goldSubmissionId || ''),
    silver: String(votes.silverSubmissionId || ''),
    bronze: String(votes.bronzeSubmissionId || ''),
  }), [votes]);
  const weeklyVoteCandidates = useMemo(() => (
    (Array.isArray(state?.entries) ? state.entries : []).map((entry) => ({
      id: String(entry?.submissionId || ''),
      answerText: String(entry?.answerText || ''),
      isMyEntry: entry?.isOwn === true,
    }))
  ), [state?.entries]);
  const weeklyMyEntry = useMemo(() => (
    weeklyVoteCandidates.find((candidate) => candidate.isMyEntry) || null
  ), [weeklyVoteCandidates]);
  const weeklyVisibleVoteCandidates = useMemo(() => (
    weeklyMyEntry
      ? weeklyVoteCandidates.filter((candidate) => candidate.id !== weeklyMyEntry.id)
      : weeklyVoteCandidates
  ), [weeklyMyEntry, weeklyVoteCandidates]);
  const weeklyCycleId = String(state?.cycle?.activeCycleId || '').trim();
  const weeklyOrderedCandidates = useMemo(() => {
    const shuffled = seededShuffle(
      weeklyVisibleVoteCandidates,
      `${weeklyCycleId}:${viewerUid}:${weeklyVisibleVoteCandidates.map((candidate) => candidate.id).join(',')}`
    );
    return weeklyMyEntry ? [weeklyMyEntry, ...shuffled] : shuffled;
  }, [viewerUid, weeklyCycleId, weeklyMyEntry, weeklyVisibleVoteCandidates]);
  const weeklyCandidateColumns = useMemo(() => [weeklyOrderedCandidates], [weeklyOrderedCandidates]);
  const handleWeeklyMedalClick = useCallback((submissionId, medal) => {
    if (state?.capabilities?.canVote !== true || busy) return;
    const nextMap = buildNextVoteMap({
      gold: String(weeklyVotesRef.current.goldSubmissionId || ''),
      silver: String(weeklyVotesRef.current.silverSubmissionId || ''),
      bronze: String(weeklyVotesRef.current.bronzeSubmissionId || ''),
    }, submissionId, medal);
    void playMedalFeedback(medal, Boolean(nextMap[medal])).catch(() => {});
    const nextVotes = {
      goldSubmissionId: nextMap.gold,
      silverSubmissionId: nextMap.silver,
      bronzeSubmissionId: nextMap.bronze,
    };
    weeklyVotesRef.current = nextVotes;
    setVotes(nextVotes);
    if (weeklyVoteSaveTimerRef.current) window.clearTimeout(weeklyVoteSaveTimerRef.current);
    weeklyVoteSaveTimerRef.current = window.setTimeout(() => {
      weeklyVoteSaveTimerRef.current = null;
      void saveVotes(nextVotes);
    }, 250);
  }, [busy, state?.capabilities?.canVote]);
  const displayPhase = view === 'entry' || view === 'vote' ? view : state?.phase;
  const deadline = state?.countdowns?.deadlineAt;
  const phaseLabel = state?.phase === 'vote' ? 'Voting closes' : 'Entries close';
  const handleWeeklyInvalidFeedback = useCallback((message = '') => {
    if (weeklyInputErrorTimerRef.current) window.clearTimeout(weeklyInputErrorTimerRef.current);
    const nextMessage = String(message || '').replace('must', 'should');
    setWeeklyInputError(nextMessage);
    if (nextMessage) {
      weeklyInputErrorTimerRef.current = window.setTimeout(() => {
        setWeeklyInputError('');
        weeklyInputErrorTimerRef.current = null;
      }, 4000);
    }
  }, []);

  useEffect(() => () => {
    if (weeklyInputErrorTimerRef.current) window.clearTimeout(weeklyInputErrorTimerRef.current);
  }, []);

  useEffect(() => {
    if (!embedded) return undefined;
    const title = weeklyTitleRef.current;
    const stage = weeklyPictureStageRef.current;
    const letters = displayPhase === 'vote' ? null : weeklyLettersRef.current;
    const borderSvg = weeklyBorderSvgRef.current;
    const borderPath = weeklyBorderPathRef.current;
    if (!title || !stage || !borderSvg || !borderPath) return undefined;
    const updateNooks = () => {
      const stageRect = stage.getBoundingClientRect();
      const stageWidth = Math.max(1, Number(stage.offsetWidth || stage.clientWidth || stageRect.width || 1));
      const stageHeight = Math.max(1, Number(stage.offsetHeight || stage.clientHeight || stageRect.height || 1));
      const scaleX = stageRect.width > 0 ? stageRect.width / stageWidth : 1;
      const scaleY = stageRect.height > 0 ? stageRect.height / stageHeight : 1;
      const titleParentRect = title.offsetParent?.getBoundingClientRect?.();
      if (titleParentRect && titleParentRect.width > 0) {
        const titleParentWidth = Math.max(1, Number(title.offsetParent?.clientWidth || titleParentRect.width));
        const titleParentScaleX = titleParentRect.width / titleParentWidth;
        const stageCenterInTitleParent = ((stageRect.left + (stageRect.width / 2)) - titleParentRect.left) / titleParentScaleX;
        title.style.left = `${stageCenterInTitleParent}px`;
      }
      const titleRect = title.getBoundingClientRect();
      const lettersElement = letters?.querySelector('[data-android-home-fit-text="letters"]');
      const lettersText = String(lettersElement?.querySelector('[data-text]')?.getAttribute('data-text') || '').trim();
      if (lettersElement && lettersText && lettersText !== '...') {
        const fit = getDailyLettersFitMobile(lettersText);
        const width = Math.max(1, Number(lettersElement.clientWidth || stageWidth || 1));
        const fontSize = Math.max(Math.min(width / (fit.compressor * 10), fit.maxFontSize), fit.minFontSize);
        lettersElement.style.fontSize = `${fontSize}px`;
        lettersElement.style.letterSpacing = fit.letterSpacing;
        lettersElement.style.wordSpacing = fit.wordSpacing;
      }
      const lettersGlyph = letters?.querySelector('.androidAppHomeStatic__lettersGlyph');
      const lettersRect = (lettersGlyph || letters)?.getBoundingClientRect();
      const card = stage.closest('.weeklyPictonymEnterCard');
      const inputWrap = card?.querySelector('.androidAppHomeEnterCard__inputWrap');
      const inputRect = inputWrap?.getBoundingClientRect();
      const topDepth = Math.max(0, Math.min(stageHeight / 2, ((titleRect.bottom - stageRect.top) / scaleY) + 9));
      const bottomOverlap = lettersRect ? (stageRect.bottom - lettersRect.top) / scaleY : 0;
      const clearanceBelowLetters = lettersRect && inputRect
        ? Math.max(0, (inputRect.top - lettersRect.bottom) / scaleY)
        : 7;
      const balancedBottomDepth = bottomOverlap + clearanceBelowLetters;
      const hasBottomNook = balancedBottomDepth > 1;
      const bottomDepth = hasBottomNook
        ? Math.max(0, Math.min(stageHeight / 2, balancedBottomDepth))
        : 0;
      const topHalf = Math.min(stageWidth / 2 - 10, (titleRect.width / scaleX / 2) + 14);
      const bottomHalf = hasBottomNook && lettersRect
        ? Math.min(stageWidth / 2 - 10, (lettersRect.width / scaleX / 2) + 16)
        : 0;
      stage.style.setProperty('--weekly-top-nook-half', `${topHalf}px`);
      stage.style.setProperty('--weekly-top-nook-depth', `${topDepth}px`);
      stage.style.setProperty('--weekly-bottom-nook-half', `${bottomHalf}px`);
      stage.style.setProperty('--weekly-bottom-nook-depth', `${bottomDepth}px`);
      card?.style.setProperty('--weekly-top-nook-depth', `${topDepth}px`);
      const path = buildRoundedNookPath({
        width: stageWidth,
        height: stageHeight,
        topHalf,
        topDepth,
        bottomHalf,
        bottomDepth,
      });
      stage.style.clipPath = `path('${path}')`;
      stage.style.webkitClipPath = `path('${path}')`;
      borderSvg.setAttribute('viewBox', `0 0 ${stageWidth} ${stageHeight}`);
      borderSvg.style.width = `${stageWidth}px`;
      borderSvg.style.height = `${stageHeight}px`;
      borderSvg.style.left = `${stage.offsetLeft}px`;
      borderSvg.style.top = `${stage.offsetTop}px`;
      borderSvg.style.right = 'auto';
      borderSvg.style.bottom = 'auto';
      borderPath.setAttribute('d', buildRoundedNookBorderPath({
        width: stageWidth,
        height: stageHeight,
        topHalf,
        topDepth,
        bottomHalf,
        bottomDepth,
      }));
    };
    updateNooks();
    const observer = new ResizeObserver(updateNooks);
    observer.observe(title);
    observer.observe(stage);
    if (letters) observer.observe(letters);
    const promptArea = stage.closest('.weeklyPictonymEnterCard__promptArea');
    if (promptArea) observer.observe(promptArea);
    const inputWrap = stage.closest('.weeklyPictonymEnterCard')?.querySelector('.androidAppHomeEnterCard__inputWrap');
    if (inputWrap) observer.observe(inputWrap);
    window.addEventListener('resize', updateNooks);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateNooks);
    };
  }, [displayPhase, embedded, loading, state?.challenge?.imageUrl]);

  useEffect(() => {
    if (displayPhase !== 'vote') setEmbeddedVoteExpanded(false);
  }, [displayPhase]);

  if (!embedded && loading) {
    return (
      <main className="pictonymPreloadScreen" aria-busy="true" aria-live="polite">
        <div className="pictonymPreloadScreen__status" role="status">
          Loading this week’s picture…
        </div>
      </main>
    );
  }

  const weeklyEntryCount = Math.max(0, Number(state?.metrics?.entryCount || 0));
  const weeklyEntryScheduleText = displayPhase === 'vote'
    ? 'Voting takes place Sat-Sun. Results and new Picto on Monday.'
    : `${weeklyEntryCount} ${weeklyEntryCount === 1 ? 'entry' : 'entries'}. Weekly open Mon-Fri before voting this weekend.`;

  if (embedded) {
    const hasSubmitted = state?.participation?.hasSubmitted === true;
    const canSubmit = state?.capabilities?.canSubmit === true;
    const submitLabel = busy
      ? (hasSubmitted ? 'Saving…' : 'Submitting…')
      : hasSubmitted
        ? (isEditing ? 'Save' : 'Edit Entry')
        : 'Submit';
    const isVotePhase = displayPhase === 'vote';
    const hasVoted = state?.participation?.hasVoted === true;
    const ownEntryText = String(state?.participation?.submissionText || '').trim();
    const quotedOwnEntryText = ownEntryText ? `“${ownEntryText}”` : 'You did not enter this week';
    const canSaveVotes = state?.capabilities?.canVote === true;
    const remainingWeeklyMedals = DAILY_VOTE_MEDALS.filter((medal) => !weeklyVoteMap[medal]);
    const remainingWeeklyMedalCount = remainingWeeklyMedals.length;

    const weeklyVoteAnswerFontSize = Math.min(27, getAndroidWinnerAnswerFontSizePx(ownEntryText) + 3);
    const weeklyVoteAnswer = ownEntryText ? (
      <span
        className="androidAppHomeStatic__featureAnswer androidAppHomeStatic__featureAnswer--voteEntry weeklyPictonymEnterCard__playerEntry"
        style={{ fontSize: `${weeklyVoteAnswerFontSize}px` }}
      >
        <span className="androidAppHomeStatic__answerGlyph" data-text={quotedOwnEntryText}>
          <span className="androidAppHomeStatic__answerInset androidAppHomeStatic__answerInset--a" aria-hidden="true">{quotedOwnEntryText}</span>
          <span className="androidAppHomeStatic__answerInset androidAppHomeStatic__answerInset--b" aria-hidden="true">{quotedOwnEntryText}</span>
          <span className="androidAppHomeStatic__answerFace">{quotedOwnEntryText}</span>
        </span>
      </span>
    ) : null;

    const prompt = (challenge, isLoading = false, showLetters = true) => (
      <div className="weeklyPictonymEnterCard__promptArea">
        <AndroidWeeklyTitle titleRef={weeklyTitleRef} />
        <AndroidWeeklyPicturePrompt
          challenge={challenge}
          loading={isLoading}
          showLetters={showLetters}
          pictureStageRef={weeklyPictureStageRef}
          lettersRef={weeklyLettersRef}
          borderSvgRef={weeklyBorderSvgRef}
          borderPathRef={weeklyBorderPathRef}
          onOpenImage={challenge ? () => setAndroidImageViewer({ src: challenge.imageUrl, alt: challenge.imageAlt }) : null}
        />
      </div>
    );

    const imageViewer = androidImageViewer ? (
      <AndroidWeeklyImageViewer image={androidImageViewer} onClose={() => setAndroidImageViewer(null)} />
    ) : null;

    if (!loading && state?.challenge && isVotePhase && state.participation.isAuthenticated) {
      return (
        <>
          <AndroidHomeVerticalSubslides
          expanded={embeddedVoteExpanded}
          onForward={() => setEmbeddedVoteExpanded(true)}
          onBack={() => setEmbeddedVoteExpanded(false)}
          canAdvance={state.capabilities.canVote === true}
          canGoBack={!busy}
          className="weeklyPictonymVoteCarousel"
          aria-label="Weekly Pictonym voting"
        >
          <div className="androidAppHomeStatic__dailyFeatureCard androidAppHomeStatic__dailyFeatureCard--enter weeklyPictonymEnterCard weeklyPictonymEnterCard--voteSummary">
            {hasVoted ? <span className="androidAppHomeStatic__featureCompleteBadge" aria-hidden="true"><Check /></span> : null}
            <div className="androidAppHomeEnterCard__form">
              {prompt(state.challenge, false, false)}
              <div className="androidAppHomeEnterCard__entryArea weeklyPictonymEnterCard__voteSummaryArea">
                <div className="androidAppHomeStatic__featureBody weeklyPictonymEnterCard__voteAnswerBlock">
                  {weeklyVoteAnswer ? (
                    <div ref={weeklyVoteAnswerRef} className="weeklyPictonymEnterCard__voteAnswerOverlap">
                      {weeklyVoteAnswer}
                    </div>
                  ) : null}
                  {!ownEntryText ? (
                    <div ref={weeklyVoteAnswerRef} className="weeklyPictonymEnterCard__voteAnswerOverlap">
                      <span className="androidAppHomeStatic__featureAnswer androidAppHomeStatic__featureAnswer--voteEntry weeklyPictonymEnterCard__emptyEntry">
                        <span className="androidAppHomeStatic__answerGlyph" data-text="You did not enter this week">
                          <span className="androidAppHomeStatic__answerInset androidAppHomeStatic__answerInset--a" aria-hidden="true">You did not enter this week</span>
                          <span className="androidAppHomeStatic__answerInset androidAppHomeStatic__answerInset--b" aria-hidden="true">You did not enter this week</span>
                          <span className="androidAppHomeStatic__answerFace">You did not enter this week</span>
                        </span>
                      </span>
                    </div>
                  ) : null}
                  <span className="androidAppHomeStatic__featureMeta weeklyPictonymEnterCard__voteMeta">
                    {Number(state?.metrics?.entryCount || 0)} {Number(state?.metrics?.entryCount || 0) === 1 ? 'entry' : 'entries'}, {Number(state?.metrics?.medalAwardCount || 0)} {Number(state?.metrics?.medalAwardCount || 0) === 1 ? 'vote' : 'votes'}
                  </span>
                </div>
                <button
                  type="button"
                  className="androidAppHomeStatic__featureCta androidAppHomeEnterCard__submit androidAppHomeVerticalNavButton androidAppHomeVerticalNavButton--down weeklyPictonymEnterCard__voteCta"
                  disabled={!state.capabilities.canVote}
                  onClick={() => setEmbeddedVoteExpanded(true)}
                  data-nav-pending-ignore="true"
                >
                  {hasVoted ? 'Change Votes' : 'Vote Now'}
                </button>
                <p className="weeklyPictonymEnterCard__schedule">{weeklyEntryScheduleText}</p>
              </div>
            </div>
          </div>
          <div className="androidAppHomeStatic__dailyFeatureCard androidAppHomeStatic__dailyFeatureCard--vote androidAppHomeVoteCard--expanded dailyVoteScreen weeklyPictonymVoteCard--expanded">
            <div className="androidAppHomeVoteCard__scroller" data-android-vertical-scroll-region>
              <div className="androidAppHomeVoteCard__top">
                <span className="androidAppHomeVoteCard__prompt">Weekly Pictonym Voting</span>
              </div>
              <DailyVoteBoard
                candidateColumns={weeklyCandidateColumns}
                effectiveLoading={loading}
                voteMap={weeklyVoteMap}
                canVote={canSaveVotes && !busy}
                requiresAuthPrompt={false}
                onMedalClick={handleWeeklyMedalClick}
                answerMinFontSize={14}
                answerMaxFontSize={16}
              />
            </div>
            {error ? <span className="androidAppHomeEnterCard__error" role="alert">{error}</span> : null}
            <div className="androidAppHomeVoteCard__footer">
              <button
                type="button"
                className="androidAppHomeVoteCard__back androidAppHomeVerticalNavButton androidAppHomeVerticalNavButton--up"
                disabled={busy}
                onClick={async () => {
                  if (weeklyVoteSaveTimerRef.current) {
                    window.clearTimeout(weeklyVoteSaveTimerRef.current);
                    weeklyVoteSaveTimerRef.current = null;
                    await saveVotes(weeklyVotesRef.current);
                  }
                  setEmbeddedVoteExpanded(false);
                }}
              >
                {busy ? 'Saving...' : 'Go Back'}
              </button>
              <span className="androidAppHomeVoteCard__status" role="status" aria-live="polite">
                <span className="androidAppHomeVoteCard__statusLine">Tap medals to cast your votes.</span>
                <span className="androidAppHomeVoteCard__statusLine androidAppHomeVoteCard__statusLine--medals">
                  {remainingWeeklyMedalCount === 0 ? (
                    <span>All done!</span>
                  ) : (
                    <>
                      <span>{remainingWeeklyMedalCount} medal{remainingWeeklyMedalCount === 1 ? '' : 's'} left to award</span>
                      <span className="androidAppHomeVoteCard__remainingMedals" aria-hidden="true">
                        {DAILY_VOTE_MEDALS.map((medal) => (
                          <Award
                            key={medal}
                            size={12}
                            className={`medal-icon medal-icon--${medal} androidAppHomeVoteCard__remainingMedal${weeklyVoteMap[medal] ? ' is-awarded' : ''}`}
                          />
                        ))}
                      </span>
                    </>
                  )}
                </span>
              </span>
            </div>
          </div>
          </AndroidHomeVerticalSubslides>
          {imageViewer}
        </>
      );
    }

    return (
      <>
        <div className="androidAppHomeStatic__dailyFeatureCard androidAppHomeStatic__dailyFeatureCard--enter weeklyPictonymEnterCard">
        {hasSubmitted ? <span className="androidAppHomeStatic__featureCompleteBadge" aria-hidden="true"><Check /></span> : null}
        {loading ? (
          <div className="androidAppHomeEnterCard__form">
            {prompt(null, true)}
            <span className="androidAppHomeStatic__featureCta">Loading…</span>
          </div>
        ) : null}
        {!loading && !state?.available ? (
          <div className="androidAppHomeEnterCard__form">
            <div className="androidAppHomeStatic__featureBody androidAppHomeEnterCard__promptBlock">
              <span className="androidAppHomeStatic__featureEyebrow">Weekly Pictonym</span>
              <span className="androidAppHomeStatic__featureCategory">This week’s picture is being prepared</span>
              <span className="androidAppHomeStatic__featureMeta">Check back shortly.</span>
            </div>
            <span className="androidAppHomeStatic__featureCta is-disabled">Unavailable</span>
          </div>
        ) : null}
        {!loading && state?.challenge ? (
          state.participation.isAuthenticated ? (
            <form className="androidAppHomeEnterCard__form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
              {prompt(state.challenge)}
              <div className="androidAppHomeEnterCard__entryArea">
                <div className={`androidAppHomeEnterCard__inputWrap${hasSubmitted && !isEditing ? ' androidAppHomeEnterCard__inputWrap--locked' : ''}`}>
                  <AcronymInput
                    acronymLetters={state.challenge.letters}
                    value={answer}
                    onChange={(event) => setAnswer(applyLeadingCaps(event.target.value))}
                    onSubmit={submit}
                    disabled={busy || !canSubmit || !isEditing}
                    placeholder="Your witty caption..."
                    useNativeKeyboard
                    useNativeCaret
                    centerPreviewOnInputText
                    inputElementRef={inputElementRef}
                    onInvalidFeedback={handleWeeklyInvalidFeedback}
                  />
                </div>
                {error ? <span className="androidAppHomeEnterCard__error" role="alert">{error}</span> : null}
                {hasSubmitted && isEditing ? (
                  <div className="androidAppHomeEnterCard__actions">
                    <button type="submit" className="androidAppHomeStatic__featureCta androidAppHomeEnterCard__submit" disabled={busy || !canSubmit} aria-busy={busy}>{submitLabel}</button>
                    <button type="button" className="androidAppHomeEnterCard__cancel" disabled={busy} onClick={cancelEdit}>Cancel</button>
                  </div>
                ) : (
                  <button
                    type={hasSubmitted ? 'button' : 'submit'}
                    className="androidAppHomeStatic__featureCta androidAppHomeEnterCard__submit"
                    disabled={busy || !canSubmit}
                    aria-busy={busy}
                    onClick={hasSubmitted ? activateEditMode : undefined}
                    data-nav-pending-target={!hasSubmitted ? true : undefined}
                  >
                    {canSubmit ? submitLabel : 'Entries Closed'}
                  </button>
                )}
                <p className={`weeklyPictonymEnterCard__schedule${weeklyInputError ? ' is-helper' : ''}`} role={weeklyInputError ? 'alert' : undefined}>
                  {weeklyInputError || weeklyEntryScheduleText}
                </p>
              </div>
            </form>
          ) : (
            <div className="androidAppHomeEnterCard__form">
              {prompt(state.challenge)}
              <Link href="/?view=login#play-game" className="androidAppHomeStatic__featureCta">Log In / Create Account to {isVotePhase ? 'Vote' : 'Enter'}</Link>
            </div>
          )
        ) : null}
        </div>
        {imageViewer}
      </>
    );
  }

  if (!loading && state?.challenge && displayPhase === 'entry') {
    const challenge = state.challenge;
    const challengeImageUrl = challenge.imageUrl;
    const hasSubmitted = state.participation?.hasSubmitted === true;
    const canSubmit = state.capabilities?.canSubmit === true;
    const submitLabel = busy
      ? (hasSubmitted ? 'Saving...' : 'Submitting...')
      : hasSubmitted ? (isEditing ? 'Save' : 'Submitted!') : 'Submit';

    return (
      <main className="dailyEnterScreen pictonymEnterScreen gameScreen gameScreen--answering">
        <div>
          <DesktopPictonymHeader challenge={challenge} />
          <DesktopPictonymChallenge
            challenge={challenge}
            imageUrl={challengeImageUrl}
            onOpenImage={() => setAndroidImageViewer({ src: challengeImageUrl, alt: challenge.imageAlt })}
          />

          <form onSubmit={(event) => { event.preventDefault(); void submit(); }} className="answerForm">
            <div className="answerFormRow">
              <div className={`dailyEnterScreen__inputWrap${hasSubmitted && !isEditing ? ' dailyEnterScreen__inputWrap--locked' : ''}`}>
                <AcronymInput
                  acronymLetters={challenge.letters}
                  value={answer}
                  onChange={(event) => setAnswer(applyLeadingCaps(event.target.value))}
                  onSubmit={submit}
                  disabled={busy || !canSubmit || !isEditing}
                  placeholder="Your witty caption..."
                  useNativeKeyboard
                  inputElementRef={inputElementRef}
                  onInvalidFeedback={handleWeeklyInvalidFeedback}
                />
                {hasSubmitted && !isEditing ? (
                  <button
                    type="button"
                    className="dailyEnterScreen__inputEditBtn"
                    disabled={busy || !canSubmit}
                    onClick={activateEditMode}
                    aria-label="Edit entry"
                    title="Edit entry"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M3 17.25V21h3.75L19.81 7.94l-3.75-3.75L3 17.25Zm2.92 2.33H5v-.92l11.06-11.06.92.92L5.92 19.58ZM20.71 5.63a1 1 0 0 0 0-1.42L18.79 2.29a1 1 0 0 0-1.42 0l-1.17 1.17 3.75 3.75 1.76-1.58Z" />
                    </svg>
                  </button>
                ) : null}
              </div>
              {state.participation?.isAuthenticated ? (
                <button type="submit" className="button buttonPrimary dailyEnterScreen__submitBtn" disabled={busy || !canSubmit || !isEditing} aria-busy={busy}>
                  <span className="dailyEnterScreen__submitBtnLabel">{canSubmit ? submitLabel : 'Entries Closed'}</span>
                </button>
              ) : (
                <Link href="/?view=login#play-game" className="button buttonPrimary">Log In / Create Account to Enter</Link>
              )}
            </div>
            {weeklyInputError ? <p className="error" role="alert">{weeklyInputError}</p> : null}
            {hasSubmitted ? (
              <div className="submission-counter">
                Submission saved. You can edit until close. Closes in {formatCountdown(deadline, now)}.
              </div>
            ) : (
              <div className="submission-counter">{weeklyEntryScheduleText}</div>
            )}
            {error ? <p className="error" role="alert">{error}</p> : null}
            {xpMessage ? <p className="submission-counter" role="status">{xpMessage}</p> : null}
          </form>

          <div className="dailyPage__bottomAction">
            <Link href="/" className="dailyPage__homeLink">
              <ArrowLeft size={16} strokeWidth={2} />
              <span>Back to Home</span>
            </Link>
          </div>

          <SiteFooter />
        </div>
        {androidImageViewer ? <AndroidWeeklyImageViewer image={androidImageViewer} onClose={() => setAndroidImageViewer(null)} /> : null}
      </main>
    );
  }

  if (!loading && state?.challenge && displayPhase === 'vote') {
    const challenge = state.challenge;
    const challengeImageUrl = challenge.imageUrl;
    const isAuthenticated = state.participation?.isAuthenticated === true;
    const canVote = isAuthenticated && state.capabilities?.canVote === true;

    return (
      <main className="dailyEnterScreen pictonymEnterScreen dailyVoteScreen pictonymVoteScreen gameScreen gameScreen--answering">
        {error || busy ? (
          <div className={`dailyVoteToast dailyVoteToast--${error ? 'error' : 'info'}`} role="status" aria-live="polite">
            {error || 'Saving…'}
          </div>
        ) : null}
        <div>
          <DesktopPictonymHeader challenge={challenge} />
          <DesktopPictonymChallenge
            challenge={challenge}
            imageUrl={challengeImageUrl}
            onOpenImage={() => setAndroidImageViewer({ src: challengeImageUrl, alt: challenge.imageAlt })}
          />

          {!isAuthenticated ? (
            <section className="dailyVoteScreen__authPrompt">
              <Link href="/?view=login#play-game" className="button buttonPrimary dailyVoteScreen__authButton">
                Log In / Create Account to Vote
              </Link>
            </section>
          ) : null}

          <DailyVoteBoard
            candidateColumns={weeklyCandidateColumns}
            effectiveLoading={false}
            voteMap={weeklyVoteMap}
            canVote={canVote && !busy}
            requiresAuthPrompt={!isAuthenticated}
            onMedalClick={handleWeeklyMedalClick}
          />

          {!canVote && isAuthenticated ? (
            <p className="dailyPage__footnote">Weekly Pictonym voting is not currently open.</p>
          ) : null}
          {!isAuthenticated ? (
            <p className="dailyPage__footnote">Weekly Pictonym voting requires a registered account. Guests can browse the answers, but cannot cast votes.</p>
          ) : null}

          <div className="dailyPage__bottomAction">
            <Link href="/" className="dailyPage__homeLink">
              <ArrowLeft size={16} strokeWidth={2} />
              <span>Back to Home</span>
            </Link>
          </div>
          <SiteFooter />
        </div>
        {androidImageViewer ? <AndroidWeeklyImageViewer image={androidImageViewer} onClose={() => setAndroidImageViewer(null)} /> : null}
      </main>
    );
  }

  return (
    <main className={`pictonymPage${embedded ? ' pictonymPage--embedded' : ''}`}>
      <header className="pictonymPage__header">
        <span className="pictonymPage__icon"><ImageIcon aria-hidden="true" /></span>
        <div><p className="pictonymPage__eyebrow">Weekly</p><h1>Weekly Pictonym{embedded && displayPhase ? ` · ${displayPhase === 'entry' ? 'Enter' : 'Vote'}` : ''}</h1></div>
        {!embedded ? <Link href="/pictonym/results" className="button buttonSecondary"><Trophy size={16} /> Results</Link> : null}
      </header>
      {loading ? <section className="pictonymPanel marketingFaqItem">Loading this week’s picture…</section> : null}
      {!loading && !state?.available ? (
        <section className="pictonymPanel marketingFaqItem"><h2>This week’s Pictonym is being prepared</h2><p>Check back shortly for a new picture and acronym.</p></section>
      ) : null}
      {!loading && state?.challenge ? (
        <>
          <section className="pictonymHero marketingFaqItem">
            <img src={state.challenge.imageUrl} alt={state.challenge.imageAlt} className="pictonymHero__image" />
            <div className="pictonymHero__meta">
              <span>{state.challenge.label}</span>
              <strong>{state.challenge.letters.split('').join(' · ')}</strong>
              <span>{phaseLabel} in {formatCountdown(deadline, now)}</span>
            </div>
          </section>
          {displayPhase === 'entry' ? (
            <section className="pictonymPanel marketingFaqItem">
              <h2>Caption the picture</h2>
              <p>Invent dialogue, context, or a joke. Each word must follow the letters in order.</p>
              {state.participation.isAuthenticated ? (
                <>
                  <AcronymInput acronymLetters={state.challenge.letters} value={answer} onChange={(event) => setAnswer(applyLeadingCaps(event.target.value))} onSubmit={submit} disabled={busy || !state.capabilities.canSubmit} useNativeKeyboard useNativeCaret />
                  <div className="pictonymActions">
                    <button className="button buttonPrimary pictonymButton" type="button" onClick={submit} disabled={busy || !state.capabilities.canSubmit}>{state.capabilities.canSubmit ? (state.participation.hasSubmitted ? 'Save Caption' : 'Enter This Week') : 'Entries Closed'}</button>
                    {state.participation.hasSubmitted ? <button className="button buttonSecondary" type="button" onClick={() => claimXp('entry')} disabled={busy}>Claim 10 XP</button> : null}
                  </div>
                </>
              ) : <Link className="button buttonPrimary pictonymButton" href="/?view=login#play-game">Log In to Enter</Link>}
            </section>
          ) : (
            <section className="pictonymPanel marketingFaqItem">
              <h2>Vote on this week’s captions</h2>
              <p>Assign gold, silver, and bronze to three different captions.</p>
              {!state.capabilities.canVote ? <p className="pictonymPhaseNotice">Voting opens Saturday. Swipe back to Weekly Enter to caption the picture now.</p> : null}
              <DailyVoteBoard
                candidateColumns={weeklyCandidateColumns}
                effectiveLoading={loading}
                voteMap={weeklyVoteMap}
                canVote={state.capabilities.canVote === true && !busy}
                requiresAuthPrompt={state.participation.isAuthenticated !== true}
                onMedalClick={handleWeeklyMedalClick}
              />
              {state.participation.isAuthenticated ? <div className="pictonymActions"><button className="button buttonPrimary pictonymButton" type="button" onClick={() => saveVotes(votes)} disabled={busy || !state.capabilities.canVote}>{state.capabilities.canVote ? (state.participation.hasVoted ? 'Update Votes' : 'Save Votes') : 'Voting Opens Saturday'}</button>{state.participation.hasVoted ? <button className="button buttonSecondary" type="button" onClick={() => claimXp('vote')} disabled={busy}>Claim 10 XP</button> : null}</div> : <Link className="button buttonPrimary pictonymButton" href="/?view=login#play-game">Log In to Vote</Link>}
            </section>
          )}
          {state.latestResult ? <aside className="pictonymLatest marketingFaqItem"><span>Latest Weekly winner</span><strong>{state.latestResult.winners?.[0]?.answerText}</strong><Link href={`/pictonym/results?cycleId=${state.latestResult.cycleId}`}>View results</Link></aside> : null}
        </>
      ) : null}
      {error ? <p className="pictonymError" role="alert">{error}</p> : null}
      {xpMessage ? <p className="pictonymSuccess" role="status">{xpMessage}</p> : null}
    </main>
  );
}
